var __spreadArrays = (this && this.__spreadArrays) || function () {
    for (var s = 0, i = 0, il = arguments.length; i < il; i++) s += arguments[i].length;
    for (var r = Array(s), k = 0, i = 0; i < il; i++)
        for (var a = arguments[i], j = 0, jl = a.length; j < jl; j++, k++)
            r[k] = a[j];
    return r;
};
import { ComponentFeature } from './internal';
import { arrayItemsDifference, arrayItemsEqual, arrayItemsIndexOf } from './utility';
var Controller = /** @class */ (function () {
    function Controller(mainComponentInstance, utilities) {
        var _a;
        var _this = this;
        this.components = (_a = {},
            _a[ComponentFeature.MAIN] = {
                instance: null,
                data: null,
                next: ComponentFeature.FILTERING
            },
            _a[ComponentFeature.FILTERING] = {
                instance: null,
                data: null,
                utility: null,
                next: ComponentFeature.PROPERTY_FILTERING
            },
            _a[ComponentFeature.PROPERTY_FILTERING] = {
                instance: null,
                data: null,
                utility: null,
                next: ComponentFeature.SORTING
            },
            _a[ComponentFeature.SORTING] = {
                instance: null,
                data: null,
                utility: null,
                next: ComponentFeature.PAGINATION
            },
            _a[ComponentFeature.PAGINATION] = {
                instance: null,
                data: null,
                utility: null,
                next: ComponentFeature.SELECTION
            },
            _a[ComponentFeature.SELECTION] = {
                instance: null,
                data: null,
                utility: null
            },
            _a);
        this.pageSizeSelector = null;
        this.wrapLinesSelector = null;
        this.lastClickedItem = null;
        this.initialized = false;
        var localMainComponent = this.components[ComponentFeature.MAIN];
        localMainComponent.instance = mainComponentInstance;
        localMainComponent.data = mainComponentInstance.__getData();
        Object.keys(utilities).forEach(function (componentName) {
            _this.components[componentName].utility = utilities[componentName];
        });
    }
    Controller.prototype.initialize = function () {
        if (!this.initialized) {
            this.initialized = true;
            this.processComponentChange(this.components[ComponentFeature.MAIN].next);
        }
    };
    Controller.prototype.deInitialize = function () {
        this.initialized = false;
    };
    Controller.prototype.setMainComponentData = function (newCollectionData) {
        var oldData = this.components[ComponentFeature.MAIN].data;
        this.components[ComponentFeature.MAIN].data = newCollectionData;
        if (this.initialized) {
            this.processComponentChange(this.components[ComponentFeature.MAIN].next, null, true, oldData);
        }
    };
    Controller.prototype.setComponent = function (name, instance) {
        var localComponent = this.components[name];
        localComponent.instance = instance;
        localComponent.data = instance ? instance.__getData() : null;
        this.processComponentChange(name);
    };
    Controller.prototype.processComponentChange = function (name, inputData, userAction, oldData) {
        if (userAction === void 0) { userAction = true; }
        var oldVisibleItems = oldData ? oldData.items : this.getVisibleItems();
        var component = this.components[name];
        if (!this.initialized) {
            // before initialization the new settings sent by each component is stored in the internal data model.
            // They will be used for the initialization processing
            component.data = inputData || component.data;
            return;
        }
        if (name === ComponentFeature.PAGINATION) {
            this.lastClickedItem = null;
        }
        var componentsChanged = this.componentProcessLoop(name, inputData, false, userAction);
        var visibleItemsChanged = !arrayItemsEqual(this.getVisibleItems(), oldVisibleItems);
        this.updateMainComponent(componentsChanged && visibleItemsChanged);
        var localMainComponentInstance = this.getComponentInstance(ComponentFeature.MAIN);
        if (visibleItemsChanged) {
            var stickyHandler = localMainComponentInstance.__stickyHandler;
            if (stickyHandler) {
                var beforeUnstick = function (onNotCanceled) {
                    return localMainComponentInstance.__fireEvent('beforeUnstick', null, { onNotCanceled: onNotCanceled });
                };
                stickyHandler.forceUnstickIfNeeded(beforeUnstick, name);
            }
        }
    };
    Controller.prototype.componentProcessLoop = function (name, inputData, fireChangeEvent, userAction) {
        if (fireChangeEvent === void 0) { fireChangeEvent = false; }
        if (userAction === void 0) { userAction = true; }
        var component = this.components[name];
        if (component.data) {
            // check whether the component is present and has been attached
            var previousData = component.data;
            // when inputData is not provided (namely when this method is called by the previous component),
            // the previously stored settings are taken into account to perform the processing
            inputData = inputData || previousData;
            var items = this.getItems(name);
            // the current settings are needed only by the sorting so far, we still provide them to any utility update method
            var nextData = component.utility.update(items, inputData, previousData);
            // Ascertain whether there are changes in the settings that have to be notified by raising an event on the
            // single component
            var notifyChange = component.utility.compare(previousData, nextData, userAction, inputData);
            component.data = nextData;
            // inform the component about the new settings and notify whether a change event has to be raised
            component.instance.__updateData(nextData, notifyChange);
            // update the global flag that informs the main component that a change event has to be raised.
            // The same flag is used throughout the full loop of processComponentChange's
            // because we want to raise the change event only once on the main component, even if more than one
            // sub-components have undergone relevant changes (notified with specific events on the sub-component itself)
            fireChangeEvent = fireChangeEvent || notifyChange;
        }
        if (component.next) {
            return this.componentProcessLoop(component.next, null, fireChangeEvent);
        }
        return fireChangeEvent;
    };
    Controller.prototype.getVisibleItems = function () {
        return this.getItems();
    };
    Controller.prototype.getItems = function (stage) {
        if (stage === void 0) { stage = ComponentFeature.MAIN; }
        // avoid useless rendering before initialization
        if (!this.initialized) {
            return [];
        }
        var itemsToReturn;
        var currentData;
        /* eslint-disable no-fallthrough */
        switch (stage) {
            case ComponentFeature.MAIN:
            case ComponentFeature.SELECTION:
                currentData = this.getComponentData(ComponentFeature.PAGINATION);
                if (currentData) {
                    itemsToReturn = currentData.items;
                }
            case ComponentFeature.PAGINATION:
                currentData = this.getComponentData(ComponentFeature.SORTING);
                if (!itemsToReturn && currentData) {
                    itemsToReturn = currentData.items;
                }
            case ComponentFeature.SORTING:
                currentData = this.getComponentData(ComponentFeature.FILTERING);
                if (!currentData) {
                    currentData = this.getComponentData(ComponentFeature.PROPERTY_FILTERING);
                }
                if (!itemsToReturn && currentData) {
                    itemsToReturn = currentData.items;
                }
            case ComponentFeature.FILTERING:
            case ComponentFeature.PROPERTY_FILTERING:
            default:
                if (!itemsToReturn) {
                    itemsToReturn = this.getComponentData(ComponentFeature.MAIN).items;
                }
        }
        /* eslint-enable no-fallthrough */
        return itemsToReturn;
    };
    Controller.prototype.updateMainComponent = function (fireEvent) {
        var localMainComponentInstance = this.getComponentInstance(ComponentFeature.MAIN);
        localMainComponentInstance.__update();
        if (fireEvent) {
            localMainComponentInstance.__fireVisibleItemsChangeEvent();
        }
    };
    Controller.prototype.getComponentData = function (name) {
        return this.components[name].data;
    };
    Controller.prototype.getComponentInstance = function (name) {
        return this.components[name].instance;
    };
    Controller.prototype.isColumnSortable = function (columnId) {
        if (!this.getComponentData(ComponentFeature.SORTING)) {
            return false;
        }
        return this.components[ComponentFeature.SORTING].utility.isColumnSortable(columnId, this.getComponentData(ComponentFeature.SORTING));
    };
    Controller.prototype.toggleAllVisibleItems = function () {
        var _a = this.getComponentData(ComponentFeature.SELECTION), selectedItems = _a.selectedItems, selectionType = _a.selectionType, trackBy = _a.trackBy, keepSelection = _a.keepSelection, isItemDisabled = _a.isItemDisabled;
        // we don't want to toggle disabled items.
        var visibleItems = this.getItems().filter(function (i) { return !isItemDisabled(i); });
        var newSelectedItems;
        if (this.areAllVisibleItemsSelected()) {
            newSelectedItems = arrayItemsDifference(selectedItems, visibleItems, trackBy);
        }
        else {
            newSelectedItems = __spreadArrays(arrayItemsDifference(visibleItems, selectedItems, trackBy), selectedItems);
        }
        this.processComponentChange(ComponentFeature.SELECTION, {
            selectedItems: newSelectedItems,
            selectionType: selectionType,
            trackBy: trackBy,
            keepSelection: keepSelection,
            isItemDisabled: isItemDisabled
        }, true);
    };
    Controller.prototype.areAllVisibleItemsSelected = function () {
        var _a = this.getComponentData(ComponentFeature.SELECTION), selectedItems = _a.selectedItems, trackBy = _a.trackBy, isItemDisabled = _a.isItemDisabled;
        // Consider that all the items are visible when all the selectable items are selected
        var visibleItems = this.getItems().filter(function (i) { return !isItemDisabled(i); });
        return visibleItems.length !== 0 && arrayItemsDifference(visibleItems, selectedItems, trackBy).length === 0;
    };
    Controller.prototype.isSomeItemSelected = function () {
        var selectedItems = this.getComponentData(ComponentFeature.SELECTION).selectedItems;
        return selectedItems.length > 0;
    };
    Controller.prototype.toggleItem = function (item, itemIndex, shiftPressed) {
        if (shiftPressed === void 0) { shiftPressed = false; }
        var data = this.getComponentData(ComponentFeature.SELECTION);
        var selectionType = data.selectionType, trackBy = data.trackBy, keepSelection = data.keepSelection, isItemDisabled = data.isItemDisabled;
        var selectedItems = data.selectedItems.slice();
        var clickedItem = arrayItemsIndexOf(selectedItems, item, trackBy);
        if (shiftPressed && this.lastClickedItem !== null && selectionType === 'multi') {
            // We instantiate Set this way to support IE11
            // IE does not have a Set(<iterable>) constructor
            var selectedSet_1 = new Set();
            selectedItems.forEach(function (item) { return selectedSet_1.add(item); });
            var items = this.getItems();
            var inc = this.lastClickedItem < itemIndex ? 1 : -1;
            for (var i = this.lastClickedItem; inc * (itemIndex - i) >= 0; i += inc) {
                if (!isItemDisabled(items[i])) {
                    if (clickedItem !== -1) {
                        selectedSet_1.delete(items[i]);
                    }
                    else {
                        selectedSet_1.add(items[i]);
                    }
                }
            }
            selectedItems = [];
            selectedSet_1.forEach(function (item) { return selectedItems.push(item); });
        }
        else {
            if (clickedItem !== -1) {
                selectedItems.splice(clickedItem, 1);
            }
            else {
                selectedItems.splice(0, 0, item);
            }
        }
        this.processComponentChange(ComponentFeature.SELECTION, {
            selectedItems: selectedItems,
            selectionType: selectionType,
            trackBy: trackBy,
            keepSelection: keepSelection,
            isItemDisabled: isItemDisabled
        }, true);
        this.lastClickedItem = itemIndex;
    };
    Controller.prototype.isItemSelected = function (item) {
        var selectionComponentData = this.getComponentData(ComponentFeature.SELECTION);
        if (selectionComponentData) {
            var selectedItems = selectionComponentData.selectedItems, trackBy = selectionComponentData.trackBy;
            return arrayItemsIndexOf(selectedItems, item, trackBy) !== -1;
        }
        return false;
    };
    Controller.prototype.isItemDisabled = function (item) {
        var selectionComponentData = this.getComponentData(ComponentFeature.SELECTION);
        if (selectionComponentData) {
            var isItemDisabled = selectionComponentData.isItemDisabled;
            return isItemDisabled(item);
        }
        return false;
    };
    Controller.prototype.isAnyVisibleItemSelectable = function () {
        var selectionComponentData = this.getComponentData(ComponentFeature.SELECTION);
        var visibleItems = this.getVisibleItems();
        var isAnyVisibleAndSelectable = visibleItems.length > 0;
        if (selectionComponentData) {
            var isItemDisabled_1 = selectionComponentData.isItemDisabled;
            isAnyVisibleAndSelectable = visibleItems.some(function (item) { return !isItemDisabled_1(item); });
        }
        return isAnyVisibleAndSelectable;
    };
    Controller.prototype.setPageSize = function (newValue, userAction) {
        var pagination = this.getComponentInstance(ComponentFeature.PAGINATION);
        if (pagination) {
            var newData = pagination.__getData();
            newData.pageSize = newValue;
            this.processComponentChange(ComponentFeature.PAGINATION, newData, userAction);
        }
        if (this.pageSizeSelector) {
            this.pageSizeSelector.__pageSize = newValue;
        }
    };
    Controller.prototype.getPageSize = function () {
        var pagination = this.getComponentInstance(ComponentFeature.PAGINATION);
        if (pagination) {
            var pageSize = pagination.__getData().pageSize;
            return pageSize;
        }
    };
    Controller.prototype.setWrapLines = function (newValue, userAction) {
        var oldValue = this.getWrapLines();
        if (userAction && oldValue !== newValue) {
            var mainComponent = this.getComponentInstance(ComponentFeature.MAIN);
            mainComponent.wrapLines = newValue;
            mainComponent.__fireWrapLinesChangeEvent();
        }
        if (this.wrapLinesSelector) {
            this.wrapLinesSelector.__wrapLines = newValue;
        }
    };
    Controller.prototype.getWrapLines = function () {
        return this.getComponentInstance(ComponentFeature.MAIN).wrapLines;
    };
    Controller.prototype.setContentSelection = function (contentSelection, userAction) {
        var mainComponent = this.getComponentInstance(ComponentFeature.MAIN);
        mainComponent.__contentSelection = contentSelection;
        this.checkContainerScrolling();
        if (userAction) {
            mainComponent.__fireContentSelectionChangeEvent(contentSelection);
        }
    };
    Controller.prototype.updateSelectionLabels = function () {
        var mainComponent = this.getComponentInstance(ComponentFeature.MAIN);
        mainComponent.__updateSelectionLabels();
    };
    Controller.prototype.moveFocus = function (index, direction) {
        var componentNode = this.getComponentInstance(ComponentFeature.MAIN).node;
        var visibleItems = this.getVisibleItems();
        var nextFocusIndex = index + direction;
        while (visibleItems[nextFocusIndex] && this.isItemDisabled(visibleItems[nextFocusIndex])) {
            nextFocusIndex += direction;
        }
        nextFocusIndex = Math.min(Math.max(-1, nextFocusIndex), visibleItems.length - 1);
        if (nextFocusIndex === -1) {
            var selectAllInput = componentNode.querySelector(".awsui-table-container .awsui-table-selection-trigger.checkbox-index-all input");
            // focus "select all" input, it will be missing in Cards
            if (selectAllInput) {
                selectAllInput.focus();
            }
        }
        else {
            // focus an ordinary item
            componentNode.querySelector(".awsui-table-selection-trigger.checkbox-index-" + nextFocusIndex + " input").focus();
        }
    };
    Controller.prototype.checkContainerScrolling = function () {
        // Safari doesn't automatically constrain the scrollLeft of an element
        // when its width increases, meaning the container can end up 'over-scrolled'
        // with blank space to the right when e.g. increasing window size
        var mainComponent = this.getComponentInstance(ComponentFeature.MAIN);
        var container = mainComponent.node.querySelector('.awsui-table-container');
        if (!container)
            return;
        var maxScroll = container.scrollWidth - container.offsetWidth;
        if (container.scrollLeft > maxScroll) {
            container.scrollLeft = maxScroll;
        }
    };
    return Controller;
}());
export default Controller;
//# sourceMappingURL=controller.js.map